import { FormFieldDto, FormFieldsetDto } from "api"
import * as yup from "yup"
import { FormFieldSettingsProps } from "../interfaces"
import { evaluateCondition, getConditionFieldIds } from "./evaluateCondition"
import { isValidIsraeliID } from "./isValidIsraeliID"

/**
 * Returns a validation schema for file uploads.
 */
function getUploadValidation(
  field: FormFieldDto,
): yup.ArraySchema<string[], yup.AnyObject, string, ""> {
  return yup
    .array()
    .of(yup.string().defined())
    .required(field.requiredErrorMessage) // Forces the array to be defined
    .test("fileSize", field.patternInvalidErrorMessage, (files) => {
      if (!files) return true
      return files.every((base64) => {
        const rawBase64 = base64?.replace(/^data:.+;base64,/, "")
        const byteLength = rawBase64
          ? Buffer.byteLength(rawBase64, "base64")
          : 0
        return byteLength <= 5000000 // 5MB limit
      })
    })
}

/**
 * Returns a validation schema for checkboxes.
 * Uses `.oneOf([true])` if the field is required.
 */
function getCheckboxValidation(
  field: FormFieldDto,
): yup.BooleanSchema<boolean | undefined, yup.AnyObject, undefined, ""> {
  let schema = yup.boolean()
  if (field.required) {
    schema = schema.oneOf([true], field.requiredErrorMessage)
  }
  return schema
}

function getDropdownValidation(
  field: FormFieldDto,
): yup.StringSchema<string | undefined, yup.AnyObject, undefined, ""> {
  let schema = yup.string()
  if (field.required) {
    schema = schema.required(field.requiredErrorMessage)
  }
  // Optionally, if you have a fixed list of allowed options, you can add:
  // schema = schema.oneOf(field.options, field.requiredErrorMessage);
  return schema
}

/**
 * Returns a validation schema for textual input.
 * Also applies pattern, maximum length, number-only, and ID number validations.
 */
function getStringValidation(
  field: FormFieldDto,
  maxLength?: string,
): yup.StringSchema<string | undefined, yup.AnyObject, undefined, ""> {
  let schema = yup.string()
  if (field.required) {
    schema = schema.required(field.requiredErrorMessage)
  }
  if (field.pattern) {
    schema = schema.matches(
      new RegExp(field.pattern),
      field.patternInvalidErrorMessage,
    )
  }
  const settings = field.settings as unknown as FormFieldSettingsProps
  if (settings?.maximumLength) {
    schema = schema.max(
      Number(settings.maximumLength),
      `${maxLength} ${settings.maximumLength}`,
    )
  }
  if (settings?.fieldType === "number") {
    schema = schema.matches(/^[0-9]*$/, "Only numeric values are allowed")
  }
  if (field.alias.toLowerCase().includes("idnumber")) {
    schema = schema
      .matches(/^\d{9}$/, field.patternInvalidErrorMessage)
      .test(
        "israeli-id-checksum",
        field.patternInvalidErrorMessage,
        (value) => {
          if (!value) return true
          return isValidIsraeliID(value)
        },
      )
  }
  return schema
}

/**
 * Applies the required logic.
 * For checkboxes and dropdowns, their required logic is handled in their own functions.
 */
function applyRequiredLogic(
  schema: yup.Schema<any>,
  field: FormFieldDto,
): yup.Schema<any> {
  if (
    field.required &&
    field.type.name !== "Checkbox" &&
    field.type.name !== "Dropdown"
  ) {
    return schema.required(field.requiredErrorMessage)
  }
  return schema
}

/**
 * Applies the child field’s condition.
 */
function applyFieldCondition(
  schema: yup.Schema<any>,
  field: FormFieldDto,
): yup.Schema<any> {
  const fieldCondition = field.condition
  const controllingIds = getConditionFieldIds(fieldCondition)
  if (!fieldCondition || controllingIds.length === 0) {
    return applyRequiredLogic(schema, field)
  }
  return schema.when(controllingIds, {
    is: (...values: any[]) => {
      const currentValues = controllingIds.reduce<Record<string, any>>(
        (acc, id, idx) => {
          acc[id] = values[idx]
          return acc
        },
        {},
      )
      return evaluateCondition(fieldCondition, currentValues)
    },
    then: (shownSchema) => {
      if (fieldCondition.actionType === "Show") {
        return applyRequiredLogic(shownSchema, field)
      } else {
        return shownSchema.notRequired().strip()
      }
    },
    otherwise: (otherwiseSchema) => {
      if (fieldCondition.actionType === "Show") {
        return otherwiseSchema.notRequired().strip()
      } else {
        return applyRequiredLogic(otherwiseSchema, field)
      }
    },
  })
}

/**
 * Applies the parent fieldset’s condition logic.
 */
function applyFieldsetCondition(
  schema: yup.Schema<any>,
  fieldset: FormFieldsetDto | undefined,
  field: FormFieldDto,
): yup.Schema<any> {
  const fieldsetCondition = fieldset?.condition
  const controllingIds = getConditionFieldIds(fieldsetCondition)
  if (!fieldsetCondition || controllingIds.length === 0) {
    return applyFieldCondition(schema, field)
  }
  return schema.when(controllingIds, {
    is: (...values: any[]) => {
      const currentValues = controllingIds.reduce<Record<string, any>>(
        (acc, id, idx) => {
          acc[id] = values[idx]
          return acc
        },
        {},
      )
      return evaluateCondition(fieldsetCondition, currentValues)
    },
    then: (visibleSchema) => {
      if (fieldsetCondition.actionType === "Show") {
        return applyFieldCondition(visibleSchema, field)
      } else {
        return visibleSchema.notRequired().strip()
      }
    },
    otherwise: (hiddenSchema) => {
      if (fieldsetCondition.actionType === "Show") {
        return hiddenSchema.notRequired().strip()
      } else {
        return applyFieldCondition(hiddenSchema, field)
      }
    },
  })
}

/**
 * Returns the full validation schema based on the field type,
 * applying type‑specific logic and then fieldset/field conditions.
 */
export const getFieldValidation = (
  field: FormFieldDto,
  parentFieldset?: FormFieldsetDto,
  maxLength?: string,
): yup.Schema<any> => {
  let validation: yup.Schema<any>

  if (field.type.supportsUploadTypes) {
    validation = getUploadValidation(field)
  } else if (field.type.name === "Checkbox") {
    validation = getCheckboxValidation(field)
  } else if (field.type.name === "Dropdown") {
    validation = getDropdownValidation(field)
  } else {
    validation = getStringValidation(field, maxLength)
  }

  validation = applyFieldsetCondition(validation, parentFieldset, field)
  return validation
}
